import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import isEqual from "lodash-es/isEqual";
import { useIntl } from "react-intl";

import { Territory } from "@mapmycustomers/shared/types/entity";
import { TerritoryDetail } from "@mapmycustomers/shared/types/entity/Territory";

import DataViewButton from "@app/component/DataView/DataViewButton";
import useMapView from "@app/component/view/utils/useMapView";
import messages from "@app/scene/map/components/TerritoriesMode/components/Sidebar/messages";
import TerritoryPolygon from "@app/scene/map/components/TerritoriesMode/components/TerritoryPolygon";
import useBoundsContext from "@app/scene/map/components/TerritoriesMode/utils/useBoundsContext";
import useTerritoryBoundsEditor from "@app/scene/map/components/TerritoriesMode/utils/useTerritoryBoundsEditor";
import {
  countPinsInTerritory,
  setDataViewData,
  setTerritoryEditFormHasChanges,
  toggleDataViewVisibility,
  updateTerritory,
} from "@app/scene/map/store/territoryMode/actions";
import {
  getTerritoryPinsInBoundsCount,
  isTerritoryDataViewLoading,
} from "@app/scene/map/store/territoryMode/selectors";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";

import BaseEditTerritorySidebar from "../BaseEditTerritorySidebar";

import EditBounds from "./EditBounds";

interface Props {
  dataViewLoading: boolean;
  onClose: () => void;
  onFormChange?: (hasChanges: boolean) => void;
  onLoadPinCount: typeof countPinsInTerritory.request;
  onToggleDataViewVisibility: typeof toggleDataViewVisibility;
  pinsCount?: number;
  setDataViewData: typeof setDataViewData;
  territory: Territory;
  updateTerritory?: typeof updateTerritory.request;
}

const EditPointsTerritoryDrawer: React.FC<Props> = ({
  dataViewLoading,
  onClose,
  onFormChange,
  onLoadPinCount,
  onToggleDataViewVisibility,
  pinsCount,
  setDataViewData,
  territory,
  updateTerritory,
}) => {
  const { viewState } = useMapView();
  const intl = useIntl();
  const analytics = useExtendedAnalytics(["Edit", "Draw"], useAnalytics());
  useEffect(() => {
    analytics.initiated();
  }, [analytics]);

  const {
    canRedo,
    canUndo,
    changeVertex,
    hasChanges: pathsChanged,
    moveTerritory,
    paths,
    redo,
    reset,
    undo,
  } = useTerritoryBoundsEditor(territory.territoryDetail.points.coordinates);

  useEffect(() => {
    setDataViewData(paths);
  }, [paths, setDataViewData]);

  const shouldShowPrompt = pathsChanged;

  useEffect(() => {
    onFormChange?.(shouldShowPrompt);
  }, [onFormChange, shouldShowPrompt]);

  useEffect(() => {
    reset(territory.territoryDetail.points.coordinates);
  }, [reset, territory]);

  // render territory dynamically
  const updatedTerritory = useMemo(
    () => ({
      ...territory,
      territoryDetail: {
        ...territory.territoryDetail,
        points: { coordinates: paths, type: "Polygon" } as TerritoryDetail["points"],
      },
    }),
    [territory, paths]
  );

  const handleSave = useCallback(() => {
    updateTerritory?.({
      onSuccess: () => {
        onClose();
        analytics.completed("", {
          changedBounds: !isEqual(
            territory.territoryDetail.points,
            updatedTerritory.territoryDetail.points
          ),
          changedColor: territory.color !== updatedTerritory.color,
        });
      },
      territory: updatedTerritory,
    });
  }, [
    analytics,
    onClose,
    territory.color,
    territory.territoryDetail.points,
    updatedTerritory,
    updateTerritory,
  ]);

  useEffect(() => {
    onLoadPinCount(paths);
  }, [
    onLoadPinCount,
    paths,
    // all deps are correct here and visibleEntities is not a mistake - we really
    // need to recalculate pin count when entity visibilities change
    // And the magic with MAP_ENTITY_TYPES.map is needed to keep number of deps
    // constant even if visibleEntities length changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...MAP_ENTITY_TYPES.map((entityType) => viewState.visibleEntities.includes(entityType)),
  ]);

  const pathIsValid = paths.length > 0;

  const handleStartOver = useCallback(() => {
    reset(territory.territoryDetail.points.coordinates);
  }, [reset, territory]);
  const { setCanRedo, setCanUndo, setListeners } = useBoundsContext();
  useEffect(() => {
    setListeners({
      onRedo: redo,
      onStartOver: handleStartOver,
      onUndo: undo,
    });
  }, [handleStartOver, setListeners, undo, redo]);
  useEffect(() => setCanUndo(canUndo), [canUndo, setCanUndo]);
  useEffect(() => setCanRedo(canRedo), [canRedo, setCanRedo]);

  return (
    <BaseEditTerritorySidebar
      count={pinsCount}
      extra={
        <DataViewButton
          entityCount={pinsCount ?? 0}
          loading={dataViewLoading}
          onClick={onToggleDataViewVisibility}
        />
      }
      onClose={onClose}
      onFormChange={onFormChange}
      onSave={handleSave}
      saveDisabled={!pathIsValid ? intl.formatMessage(messages.nothingDrawn) : false}
      shouldShowPrompt={shouldShowPrompt}
    >
      <EditBounds />
      <TerritoryPolygon
        onChangeVertex={changeVertex}
        onMoveTerritory={moveTerritory}
        territory={updatedTerritory}
      />
    </BaseEditTerritorySidebar>
  );
};

const mapStateToProps = (state: RootState) => ({
  dataViewLoading: isTerritoryDataViewLoading(state),
  pinsCount: getTerritoryPinsInBoundsCount(state),
});

const mapDispatchToProps = {
  onFormChange: setTerritoryEditFormHasChanges,
  onLoadPinCount: countPinsInTerritory.request,
  onToggleDataViewVisibility: toggleDataViewVisibility,
  setDataViewData,
  updateTerritory: updateTerritory.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditPointsTerritoryDrawer);
